import { Checkbox, FormControlLabel, FormGroup, styled, Theme } from '@nexelem/deprecated-theme';
import { kebabCase } from 'lodash';
import { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, checked }) => {
  const typedTheme = theme as Theme;
  const { getColor, hexToRGB } = theme.palette;
  const { checkboxListItem } = typedTheme.client.lists;

  const basicStyles = {
    marginRight: 0,
    marginLeft: 0,
    '& > span:first-child': {
      padding: theme.spacing(3.5, 2, 3.5, 4),
      '& .MuiSvgIcon-root': {
        fontSize: theme.typography.pxToRem(18),
        color: getColor(checkboxListItem.defaultState.svgColor),
      },
    },
    'span:last-child': {
      ...typedTheme.typography.sourceSansPro16Regular,
      color: getColor(checkboxListItem.defaultState.color),
    },
    '&:hover': {
      backgroundColor: hexToRGB(getColor(checkboxListItem.hover.backgroundColor), 0.5),
    },
    '.Mui-checked': {
      '& .MuiSvgIcon-root.MuiSvgIcon-fontSizeSmall': {
        color: getColor(checkboxListItem.checked.svgColor),
      },
    },
  };
  if (checked) {
    return {
      ...basicStyles,
      backgroundColor: hexToRGB(getColor(checkboxListItem.checked.backgroundColor), 0.9),
    };
  }

  return {
    ...basicStyles,
  };
});

export type CheckboxListProps<T = string> = {
  readonly options: T[];
  readonly value: T[];
  readonly getOptionLabel?: (value: T) => string;
  readonly renderLabel?: (value: T) => ReactNode;
  readonly getOptionId?: (option: T) => string | number;
  onChange: (value: T[]) => void;
};

export function CheckboxList<T = string>({
  onChange,
  options,
  value,
  renderLabel,
  getOptionId,
  getOptionLabel = (value: T): string => value as string,
}: CheckboxListProps<T>) {
  const { t } = useTranslation();

  const handleChange = (option: T) => (event: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
    if (isChecked) {
      return onChange([...value, option]);
    }
    const getIdentifies = getOptionId || getOptionLabel;

    return onChange(value.filter((v) => getIdentifies(v) !== getIdentifies(option)));
  };

  return (
    <FormGroup>
      {options.length > 0 &&
        options.map((option) => {
          const label = getOptionLabel(option);
          const key = kebabCase(label);
          return (
            <StyledFormControlLabel
              control={<Checkbox disableRipple size="small" onChange={handleChange(option)} />}
              key={key}
              label={renderLabel ? renderLabel(option) : label}
              checked={
                getOptionId
                  ? value.map(getOptionId).includes(getOptionId(option))
                  : value.map(getOptionLabel).includes(label)
              }
            />
          );
        })}
      {options.length === 0 && (
        <div className="p-4 text-center">
          <span className="text-center text-s16 text-text-secondary">{t('components.lists.emptyState')}</span>
        </div>
      )}
    </FormGroup>
  );
}
